// FormulaInput.js - Input che permette formule matematiche (es: 2,40*2+5)
import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';

/**
 * Valuta una formula matematica in modo sicuro
 * Supporta: numeri con virgola italiana, +, -, *, /, parentesi
 * Esempio: "2,40*2+5" → 9.8
 */
export const evaluateFormula = (formula) => {
  if (!formula || formula.trim() === '') return { value: 0, error: null, isFormula: false };
  
  const trimmed = formula.trim();
  
  // Verifica se è una formula (contiene operatori matematici)
  const hasOperators = /[\+\-\*\/]/.test(trimmed) && trimmed.length > 1;
  // Esclude numeri negativi singoli come "-5"
  const isJustNegativeNumber = /^-?\d+([,\.]\d+)?$/.test(trimmed);
  
  if (!hasOperators || isJustNegativeNumber) {
    // È un numero semplice, non una formula
    const normalized = trimmed.replace(',', '.');
    const num = parseFloat(normalized);
    return { 
      value: isNaN(num) ? 0 : num, 
      error: null, 
      isFormula: false 
    };
  }
  
  try {
    // Normalizza: sostituisce virgole con punti
    let normalized = trimmed
      .replace(/,/g, '.')
      .replace(/\s+/g, '') // rimuovi spazi
      .replace(/×/g, '*')  // supporta simbolo moltiplicazione
      .replace(/÷/g, '/'); // supporta simbolo divisione
    
    // Verifica che contenga solo caratteri validi
    if (!/^[\d\.\+\-\*\/\(\)]+$/.test(normalized)) {
      return { value: 0, error: 'Caratteri non validi', isFormula: true };
    }
    
    // Verifica parentesi bilanciate
    let depth = 0;
    for (const char of normalized) {
      if (char === '(') depth++;
      if (char === ')') depth--;
      if (depth < 0) return { value: 0, error: 'Parentesi non bilanciate', isFormula: true };
    }
    if (depth !== 0) return { value: 0, error: 'Parentesi non bilanciate', isFormula: true };
    
    // Parser matematico sicuro (senza eval/Function)
    const safeEvaluate = (expr) => {
      // Tokenizza l'espressione
      const tokens = expr.match(/(\d+\.?\d*|[\+\-\*\/\(\)])/g);
      if (!tokens) return NaN;
      
      let pos = 0;
      
      const parseNumber = () => {
        if (pos >= tokens.length) return NaN;
        const num = parseFloat(tokens[pos]);
        if (!isNaN(num)) {
          pos++;
          return num;
        }
        return NaN;
      };
      
      const parseFactor = () => {
        if (tokens[pos] === '(') {
          pos++; // skip (
          const result = parseExpression();
          pos++; // skip )
          return result;
        }
        // Handle negative numbers
        if (tokens[pos] === '-') {
          pos++;
          return -parseFactor();
        }
        return parseNumber();
      };
      
      const parseTerm = () => {
        let result = parseFactor();
        while (pos < tokens.length && (tokens[pos] === '*' || tokens[pos] === '/')) {
          const op = tokens[pos++];
          const right = parseFactor();
          result = op === '*' ? result * right : result / right;
        }
        return result;
      };
      
      const parseExpression = () => {
        let result = parseTerm();
        while (pos < tokens.length && (tokens[pos] === '+' || tokens[pos] === '-')) {
          const op = tokens[pos++];
          const right = parseTerm();
          result = op === '+' ? result + right : result - right;
        }
        return result;
      };
      
      return parseExpression();
    };
    
    const result = safeEvaluate(normalized);
    
    if (typeof result !== 'number' || !isFinite(result)) {
      return { value: 0, error: 'Risultato non valido', isFormula: true };
    }
    
    // Arrotonda a 4 decimali per evitare errori floating point
    return { 
      value: Math.round(result * 10000) / 10000, 
      error: null, 
      isFormula: true 
    };
  } catch (e) {
    return { value: 0, error: 'Formula non valida', isFormula: true };
  }
};

/**
 * Formatta un numero con virgola italiana
 */
const formatNumber = (num) => {
  if (num === null || num === undefined) return '';
  // Rimuovi decimali inutili (es: 10.0 → 10)
  const formatted = parseFloat(num.toFixed(4));
  return formatted.toString().replace('.', ',');
};

/**
 * FormulaInput - Input per quantità con supporto formule
 * 
 * Props:
 * - value: valore numerico attuale
 * - formula: formula stringa salvata (opzionale)
 * - onChange: callback (value, formula) => void
 * - className: classi CSS aggiuntive
 * - placeholder: placeholder input
 */
const FormulaInput = ({ 
  value = 0, 
  formula: savedFormula = '', 
  onChange, 
  className = '',
  placeholder = '1',
  ...props 
}) => {
  // Stato interno: mostra la formula durante l'editing, altrimenti il valore
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  
  // Inizializza/aggiorna il valore visualizzato
  useEffect(() => {
    if (!isEditing) {
      // Quando non in editing, mostra il valore numerico formattato
      setInputValue(value ? formatNumber(value) : '');
    }
  }, [value, isEditing]);
  
  const handleFocus = () => {
    setIsEditing(true);
    // Se c'è una formula salvata, mostrala per l'editing
    if (savedFormula) {
      setInputValue(savedFormula);
    }
  };
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Valida in tempo reale
    const { error: calcError } = evaluateFormula(newValue);
    setError(calcError);
  };
  
  const handleBlur = () => {
    confirmValue();
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmValue();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      // Annulla modifiche
      setIsEditing(false);
      setInputValue(value ? formatNumber(value) : '');
      setError(null);
      inputRef.current?.blur();
    }
  };
  
  const confirmValue = () => {
    setIsEditing(false);
    
    if (!inputValue.trim()) {
      // Campo vuoto = 0
      onChange?.(0, '');
      setError(null);
      return;
    }
    
    const { value: calculatedValue, error: calcError, isFormula } = evaluateFormula(inputValue);
    
    if (calcError) {
      setError(calcError);
      // Non aggiornare il valore se c'è errore
      return;
    }
    
    setError(null);
    
    // Salva: valore calcolato + formula (se è una formula)
    if (isFormula) {
      onChange?.(calculatedValue, inputValue.trim());
    } else {
      onChange?.(calculatedValue, '');
    }
    
    // Mostra il valore calcolato
    setInputValue(formatNumber(calculatedValue));
  };
  
  return (
    <Input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${className} ${error ? 'border-red-400 focus:ring-red-400' : ''}`}
      placeholder={placeholder}
      title={error || (savedFormula ? `Formula: ${savedFormula}` : '')}
      {...props}
    />
  );
};

export default FormulaInput;
